import { createAsyncThunk } from '@reduxjs/toolkit';
import { getNestedRegionsRequest } from './geoApi';
import { DEFAULT_COUNTRY_ISO2, resolveCountryIso2 } from './geoConstants';

/**
 * Loads the region → commune tree for a country, cached per country code.
 *
 * Shared by every module that needs addresses, so the tree is fetched once per
 * country and reused; the `condition` below skips redundant requests.
 *
 * @param {string} [countryIso2=DEFAULT_COUNTRY_ISO2] - ISO2 country code
 */
export const fetchRegions = createAsyncThunk(
  'geo/fetchRegions',
  async (countryIso2 = DEFAULT_COUNTRY_ISO2, { rejectWithValue }) => {
    try {
      const response = await getNestedRegionsRequest(countryIso2);
      return { countryIso2, regions: response.data || [] };
    } catch (error) {
      console.error(`Error fetching nested regions for ${countryIso2}:`, error);
      return rejectWithValue('Error al cargar la estructura geográfica');
    }
  },
  {
    // Defaults are repeated here because `condition` receives the raw argument.
    condition: (countryIso2 = DEFAULT_COUNTRY_ISO2, { getState }) => {
      const { regionsByCountry, loadingByCountry } = getState().geo;
      const key = resolveCountryIso2(countryIso2);

      // Skipping on the cache alone is not enough: two components mounting in
      // the same tick — or StrictMode's double mount in development — both see
      // an empty cache and fire. The Mexican tree weighs around 18 MB, so a
      // duplicate request is worth guarding against explicitly.
      return !regionsByCountry[key] && !loadingByCountry[key];
    },
  }
);
